// Extracted from original/src/App.tsx:36353-37948 (`activeTab === "match"`, ADR-08 §2.1 "copy, then cut logic"):
//   sub-tab switch "مطابقة ورديات الموظفين" / "تسوية نقدية اليوم"           :36362-36389
//   active shift card: header, close button, metrics, expected / profits, per-box summary :36395-36661
//   "no open shift" empty state + "فتح وردية جديدة"                       :36662-36755
//   shift log: admin month totals, filters, table                          :36758-37262
//   daily tab: pending day card + metrics + live comparison grid (admin)   :37265-37811
//   daily empty state + "تسجيل رصيد بداية اليوم"                           :37812-37843
//   day log table                                                          :37844-37946
// Kept: every class string, icon and size, the motion frame, the card colours, the pulse dots, the dashed empty
// states, the table columns and their cell formats ("+"/"-" signs, " عراقي" / " دولار" suffixes, "$" prefixes),
// the "✓ مطابق تماماً" pill, the admin-only blocks and the dollar / wallets / financials / debts permission gates.
// Cut: `getShiftStatistics` / `getDayMatchStatistics` (App.tsx:680-982: operator-name text, UTC string dates,
// non-cash legs counted, F-168 – F-172) → the server's read model `GET /shifts` / `GET /days` over the ledger
// (D-298: `drawerFigures()` / `dayFigures()`), passed in as preformatted labels; `currentMonthShifts` /
// `filteredShifts` (App.tsx:597-636, UTC dates) → server filters and the office month (D-122); the open / close
// handlers and their browser-storage records (App.tsx:1080-1631, R-S11) → `onOpenShift` / `onCloseShift` /
// `onOpenDay` / `onCloseDay` callbacks (the container's dialogs, PIN and API).
// Changed by decision: one open shift per **drawer** (D-120): every visible open shift has its own card; the empty
// state shows when none is open and the "فتح وردية جديدة" button stays under the cards while an employee without
// an open shift can still get one; the close button follows D-303 (owner or `open_shift_for_other`); the expected
// figure is the drawer's ledger balance (D-298), the count is typed (no pre-fill, D-120); the stored difference and
// its approval state are shown per currency (D-122, D-299, D-304: pending / approved / rejected); profits per
// currency (D-098; old: USD profits never counted, R-S5); a day not closed on its own date shows "لم تُغلق" (D-302);
// the day's figures are the vault and all its custodies in the office day (D-121); the opening shown is the book.
// Added: the "differences awaiting approval" card (D-304) and data-testid attributes.
import { motion } from 'motion/react';
import { Calendar, CheckCircle, FileText, Info, ShieldCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';

export type MatchTab = 'shifts' | 'daily';
export type MatchCountStatus = 'matched' | 'pending' | 'approved' | 'rejected';

/** Two preformatted figures (no currency symbol); `usd` is shown only with the `dollar` permission. */
export interface MatchMoney {
  readonly iqd: string;
  readonly usd: string;
}

export interface MatchBoxLine {
  readonly name: string;
  readonly inIqd: string;
  readonly outIqd: string;
  readonly inUsd: string;
  readonly outUsd: string;
  readonly hasUsd: boolean;
}

export interface MatchActiveShift {
  readonly id: string;
  readonly owner: string;
  readonly startedLabel: string;
  readonly opening: MatchMoney;
  readonly other: MatchMoney;
  readonly receipts: MatchMoney;
  readonly outflows: MatchMoney;
  readonly expected: MatchMoney;
  /** Approved differences + corrections of the window, signed labels; null when both are zero. */
  readonly adjustments: MatchMoney | null;
  /** Differences of earlier counts still waiting (D-299 (b)); null when none. */
  readonly pending: MatchMoney | null;
  readonly operationsCount: number;
  readonly expensesCount: number;
  readonly profitsIqd: string;
  readonly profitsUsd: string | null;
  readonly boxes: readonly MatchBoxLine[];
  readonly canClose: boolean;
}

/** One currency of a stored difference: signed label, sign and approval state. */
export interface MatchDiff {
  readonly label: string;
  readonly negative: boolean;
  readonly zero: boolean;
  readonly status: MatchCountStatus | null;
}

export interface MatchShiftRow {
  readonly id: string;
  readonly owner: string;
  readonly startTime: string;
  readonly startDate: string;
  readonly endTime: string | null;
  readonly open: boolean;
  readonly opening: MatchMoney;
  readonly expected: MatchMoney;
  readonly counted: MatchMoney | null;
  readonly diffIqd: MatchDiff;
  readonly diffUsd: MatchDiff;
  readonly profitsIqd: string;
}

export interface MatchMonthTotals {
  readonly opening: MatchMoney;
  readonly closing: MatchMoney;
  readonly profits: MatchMoney;
  readonly netIqd: string;
  readonly netIqdNegative: boolean;
  readonly netUsd: string;
  readonly netUsdNegative: boolean;
}

export interface MatchGridRow {
  readonly key: string;
  readonly name: string;
  readonly usd: boolean;
  readonly opening: string;
  readonly current: string;
  readonly delta: string;
  readonly deltaSign: 1 | 0 | -1;
}

export interface MatchActiveDay {
  readonly dateLabel: string;
  readonly opening: MatchMoney;
  readonly receipts: MatchMoney;
  readonly outflows: MatchMoney;
  readonly expected: MatchMoney;
  /** Admin live grid (old "المقارنة الفورية"); null hides it (non-admin or not loaded). */
  readonly grid: readonly MatchGridRow[] | null;
}

export interface MatchDayRow {
  readonly id: string;
  readonly date: string;
  readonly open: boolean;
  readonly expired: boolean;
  readonly opening: MatchMoney;
  readonly expected: MatchMoney;
  readonly counted: MatchMoney | null;
  readonly diffIqd: MatchDiff;
  readonly diffUsd: MatchDiff;
}

export interface MatchPendingRow {
  readonly id: string;
  readonly scopeKey: string;
  readonly reopened: boolean;
  readonly responsible: string;
  readonly countedAtLabel: string;
  readonly currencyUsd: boolean;
  readonly expected: string;
  readonly counted: string;
  readonly difference: string;
  readonly negative: boolean;
  readonly canDecide: boolean;
}

export interface MatchProps {
  readonly tab: MatchTab;
  readonly onTabChange: (t: MatchTab) => void;
  readonly showUsd: boolean;
  readonly showWallets: boolean;
  readonly showProfits: boolean;
  readonly showDebts: boolean;
  readonly isAdmin: boolean;
  // shifts
  readonly activeShifts: readonly MatchActiveShift[];
  readonly canOpenShift: boolean;
  readonly onOpenShift: () => void;
  readonly onCloseShift: (id: string) => void;
  readonly hasShifts: boolean;
  readonly monthTotals: MatchMonthTotals | null;
  readonly employees: ReadonlyArray<{ readonly id: string; readonly username: string }>;
  readonly employeeFilter: string;
  readonly onEmployeeFilter: (v: string) => void;
  readonly fromDate: string;
  readonly onFromDate: (v: string) => void;
  readonly toDate: string;
  readonly onToDate: (v: string) => void;
  readonly onResetFilters: () => void;
  readonly shiftRows: readonly MatchShiftRow[];
  readonly onShiftReport: (id: string) => void;
  // daily
  readonly activeDay: MatchActiveDay | null;
  readonly canOpenDay: boolean;
  readonly onOpenDay: () => void;
  readonly onCloseDay: () => void;
  readonly dayRows: readonly MatchDayRow[];
  readonly onDayReport: (id: string) => void;
  // approvals (D-304)
  readonly pendingRows: readonly MatchPendingRow[];
  readonly onApprove: (id: string) => void;
  readonly onReject: (id: string) => void;
}

export function Match(props: MatchProps) {
  const { t } = useI18n();
  const matchTab = props.tab;
  const showUsd = props.showUsd;

  const statusBadge = (s: MatchCountStatus | null): ReactNode => {
    if (!s || s === 'matched') return null;
    const cls =
      s === 'pending'
        ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-600'
        : s === 'approved'
          ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600'
          : 'bg-rose-50 dark:bg-rose-950/40 text-rose-600';
    return <span className={`ms-1 px-1.5 py-0.5 rounded-full text-[9px] font-black ${cls}`} data-testid={`diff-status-${s}`}>{t(`mt.status_${s}`)}</span>;
  };

  const diffCell = (iqd: MatchDiff, usd: MatchDiff, matchedKey: string): ReactNode =>
    iqd.zero && usd.zero ? (
      <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 rounded-full text-[10px] font-black">{t(matchedKey)}</span>
    ) : (
      <div className="space-y-0.5 text-[11px]">
        <p className={!iqd.negative ? 'text-emerald-600' : 'text-red-600'}>
          {iqd.label} {t('mt.iqd_word')}
          {statusBadge(iqd.status)}
        </p>
        {showUsd && (
          <p className={!usd.negative ? 'text-emerald-600' : 'text-red-600'}>
            {usd.label} {t('mt.usd_word')}
            {statusBadge(usd.status)}
          </p>
        )}
      </div>
    );

  const pendingCard =
    props.pendingRows.length > 0 ? (
      <div className="premium-card p-8 border-l-4 border-l-amber-500" data-testid="match-pending">
        <h3 className="text-xl font-black mb-2 uppercase tracking-tight flex items-center gap-2">
          <ShieldCheck className="text-amber-500" size={20} />
          {t('mt.pending_title')}
        </h3>
        <p className="text-xs text-slate-400 mb-4">{t('mt.pending_hint')}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-100 dark:border-[#30363d] text-slate-400 dark:text-[#8b949e] font-black uppercase tracking-widest text-[10px]">
                <th className="py-4 px-2">{t('mt.pcol_scope')}</th>
                <th className="py-4 px-2">{t('mt.pcol_responsible')}</th>
                <th className="py-4 px-2">{t('mt.pcol_expected')}</th>
                <th className="py-4 px-2">{t('mt.pcol_counted')}</th>
                <th className="py-4 px-2 text-center">{t('mt.pcol_difference')}</th>
                <th className="py-4 px-2 text-center">{t('mt.pcol_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {props.pendingRows.map((p) => (
                <tr key={p.id} className="border-b border-gray-50 dark:border-[#161b22] hover:bg-slate-50/50 dark:hover:bg-[#0d1117]/50 font-medium" data-testid="pending-row">
                  <td className="py-4 px-2 font-black text-slate-800 dark:text-white">
                    {t(p.scopeKey)}
                    {p.reopened && <span className="ms-1 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-slate-100 dark:bg-slate-800 text-slate-500">{t('mt.reopened')}</span>}
                    <p className="text-[10px] text-slate-400 font-mono">{p.countedAtLabel}</p>
                  </td>
                  <td className="py-4 px-2 font-bold text-slate-700 dark:text-slate-300">{p.responsible}</td>
                  <td className="py-4 px-2 font-mono text-indigo-600 dark:text-indigo-400 font-black">{p.currencyUsd ? `$${p.expected}` : p.expected}</td>
                  <td className="py-4 px-2 font-mono text-slate-800 dark:text-white font-black">{p.currencyUsd ? `$${p.counted}` : p.counted}</td>
                  <td className={`py-4 px-2 text-center font-mono font-black ${p.negative ? 'text-red-600' : 'text-emerald-600'}`}>
                    {p.difference} {p.currencyUsd ? t('mt.usd_word') : t('mt.iqd_word')}
                  </td>
                  <td className="py-4 px-2 text-center">
                    {p.canDecide ? (
                      <div className="inline-flex gap-2">
                        <button type="button" onClick={() => props.onApprove(p.id)} data-testid="pending-approve" className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[10px] font-black transition-all">
                          {t('mt.btn_approve')}
                        </button>
                        <button type="button" onClick={() => props.onReject(p.id)} data-testid="pending-reject" className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-rose-100 hover:text-rose-600 rounded-xl text-[10px] font-black transition-all">
                          {t('mt.btn_reject')}
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] bg-amber-50 dark:bg-amber-950/40 text-amber-600 px-2.5 py-1 rounded-full">{t('mt.status_pending')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ) : null;

  const activeShiftCard = (activeShift: MatchActiveShift) => (
    <div key={activeShift.id} className="premium-card p-8 border-l-4 border-l-emerald-500 relative overflow-hidden" data-testid="active-shift">
      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full -mr-16 -mt-16 blur-2xl pointer-events-none" />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-6 border-b border-slate-100 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-3">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <h3 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">{t('mt.active_shift', { name: activeShift.owner })}</h3>
          </div>
          <p className="text-xs text-slate-400 dark:text-[#8b949e] font-mono mt-1">
            {t('mt.started')} {activeShift.startedLabel}
          </p>
        </div>
        {activeShift.canClose && (
          <button
            type="button"
            id="btn-reconcile-shift"
            onClick={() => props.onCloseShift(activeShift.id)}
            className="w-full md:w-auto bg-emerald-600 text-white px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
          >
            {t('mt.btn_close_shift')}
          </button>
        )}
      </div>
      {/* Shift Real-time Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-6">
        <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
          <span className="text-[10px] font-bold text-slate-400 dark:text-[#8b949e] uppercase tracking-widest block mb-2">{t('mt.opening_till')}</span>
          <div className="space-y-1 font-mono">
            <p className="text-lg font-black text-slate-800 dark:text-white">
              {activeShift.opening.iqd} د.ع
            </p>
            {showUsd && <p className="text-sm font-black text-blue-600 dark:text-blue-400">${activeShift.opening.usd}</p>}
          </div>
        </div>
        {props.showWallets && (
          <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
            <span className="text-[10px] font-bold text-slate-400 dark:text-[#8b949e] uppercase tracking-widest block mb-2">{t('mt.other_opening')}</span>
            <div className="space-y-1 font-mono">
              <p className="text-lg font-black text-slate-700 dark:text-slate-300">
                {activeShift.other.iqd} د.ع
              </p>
              {showUsd && <p className="text-sm font-black text-slate-500">${activeShift.other.usd}</p>}
            </div>
          </div>
        )}
        <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest block mb-2">{t('mt.receipts')}</span>
          <div className="space-y-1 font-mono">
            <p className="text-lg font-black text-emerald-600 dark:text-emerald-400">+{activeShift.receipts.iqd} د.ع</p>
            {showUsd && <p className="text-sm font-black text-emerald-500">${activeShift.receipts.usd}</p>}
          </div>
        </div>
        <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
          <span className="text-[10px] font-bold text-red-600 dark:text-red-400 uppercase tracking-widest block mb-2">{t('mt.outflows')}</span>
          <div className="space-y-1 font-mono">
            <p className="text-lg font-black text-red-600 dark:text-red-400">-{activeShift.outflows.iqd} د.ع</p>
            {showUsd && <p className="text-sm font-black text-red-500">${activeShift.outflows.usd}</p>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-6">
        {/* Theoretical Closing Drawer Match */}
        <div className="p-6 bg-primary-50/30 dark:bg-primary-950/10 rounded-2xl border border-primary-100/50 dark:border-primary-900/30 col-span-1 lg:col-span-2">
          <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest block mb-2">{t('mt.expected_till')}</span>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="space-y-1 font-mono">
              <p className="text-3xl font-black text-primary-700 dark:text-primary-400 digital-glow" data-testid="shift-expected-iqd">
                {activeShift.expected.iqd} د.ع
              </p>
              {showUsd && <p className="text-xl font-black text-primary-500">${activeShift.expected.usd}</p>}
            </div>
            <div className="text-right text-xs font-bold text-slate-500 dark:text-[#8b949e]">
              <p>
                {t('mt.movements_count')} <span className="font-mono text-slate-800 dark:text-white font-black">{activeShift.operationsCount}</span>
              </p>
              <p className="mt-1">
                {t('mt.expenses_count')} <span className="font-mono text-slate-800 dark:text-white font-black">{activeShift.expensesCount}</span>
              </p>
              {activeShift.adjustments && (
                <p className="mt-1">
                  {t('mt.corrections')} <span className="font-mono text-slate-800 dark:text-white font-black" dir="ltr">{activeShift.adjustments.iqd}{showUsd ? ` / $${activeShift.adjustments.usd}` : ''}</span>
                </p>
              )}
              {activeShift.pending && (
                <p className="mt-1 text-amber-600">
                  {t('mt.pending_in_basis')} <span className="font-mono font-black" dir="ltr">{activeShift.pending.iqd}{showUsd ? ` / $${activeShift.pending.usd}` : ''}</span>
                </p>
              )}
            </div>
          </div>
        </div>
        {/* Profits card */}
        {props.showProfits && (
          <div className="p-6 bg-amber-50/30 dark:bg-amber-950/10 rounded-2xl border border-amber-100/50 dark:border-amber-900/30">
            <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest block mb-2">{t('mt.profits_card')}</span>
            <p className="text-3xl font-black text-amber-700 dark:text-amber-400 font-mono digital-glow">{activeShift.profitsIqd} د.ع</p>
            {showUsd && activeShift.profitsUsd && <p className="text-xl font-black text-amber-600 dark:text-amber-400 font-mono" dir="ltr">${activeShift.profitsUsd}</p>}
            <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mt-2 block">{t('mt.profits_hint')}</span>
          </div>
        )}
      </div>
      {/* Individual Till Details */}
      {props.showWallets && activeShift.boxes.length > 0 && (
        <div className="mt-8">
          <h4 className="text-xs font-black text-slate-400 dark:text-[#8b949e] mb-4 uppercase tracking-widest">{t('mt.till_breakdown')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeShift.boxes.map((w, idx) => (
              <div key={idx} className="p-4 bg-white dark:bg-[#070a0e] rounded-xl border border-slate-100 dark:border-[#30363d] text-xs">
                <p className="font-black text-slate-800 dark:text-white mb-2">{w.name}</p>
                <div className="space-y-1 text-slate-500 dark:text-[#8b949e] font-mono">
                  <div className="flex justify-between">
                    <span>{t('mt.in_iqd')}</span>
                    <span className="font-black text-emerald-600">+{w.inIqd}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('mt.out_iqd')}</span>
                    <span className="font-black text-red-600">-{w.outIqd}</span>
                  </div>
                  {showUsd && w.hasUsd && (
                    <>
                      <div className="flex justify-between pt-1 border-t border-slate-50 dark:border-[#161b22] mt-1">
                        <span>{t('mt.in_usd')}</span>
                        <span className="font-black text-emerald-600">+${w.inUsd}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('mt.out_usd')}</span>
                        <span className="font-black text-red-600">-${w.outUsd}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const openShiftButton = (
    <button
      type="button"
      id="btn-open-shift"
      onClick={props.onOpenShift}
      className="bg-primary-600 text-white px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-primary-500 transition-all shadow-md active:scale-95"
    >
      {t('mt.btn_open_shift')}
    </button>
  );

  const m = props.monthTotals;
  const activeDay = props.activeDay;

  return (
    <motion.div key="match" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8" data-testid="match-screen">
      {/* Tabs for Shifts VS Day matching */}
      <div className="flex gap-4 p-1 bg-slate-100 dark:bg-slate-900/50 rounded-2xl max-w-md">
        <button
          type="button"
          id="tab-shifts-reconciliation"
          onClick={() => props.onTabChange('shifts')}
          className={`flex-1 py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
            matchTab === 'shifts' ? 'bg-white dark:bg-[#0d1117] text-primary-600 dark:text-primary-400 shadow-premium' : 'text-slate-500 dark:text-[#8b949e] hover:text-slate-800'
          }`}
        >
          {t('mt.tab_shifts')}
        </button>
        <button
          type="button"
          id="tab-daily-settlement"
          onClick={() => props.onTabChange('daily')}
          className={`flex-1 py-3 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${
            matchTab === 'daily' ? 'bg-white dark:bg-[#0d1117] text-primary-600 dark:text-primary-400 shadow-premium' : 'text-slate-500 dark:text-[#8b949e] hover:text-slate-800'
          }`}
        >
          {t('mt.tab_daily')}
        </button>
      </div>
      {pendingCard}
      {matchTab === 'shifts' ? (
        <div className="space-y-8">
          {/* Current Active Shift(s) */}
          {props.activeShifts.length > 0 ? (
            <>
              {props.activeShifts.map(activeShiftCard)}
              {props.canOpenShift && <div className="flex justify-center">{openShiftButton}</div>}
            </>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-900/10 border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] flex flex-col items-center justify-center text-center gap-4" data-testid="no-shift">
              <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-lg text-slate-400">
                <CheckCircle size={32} />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 dark:text-white">{t('mt.no_shift')}</h3>
                <p className="text-xs text-slate-400 mt-1">{t('mt.no_shift_hint')}</p>
              </div>
              {props.canOpenShift && openShiftButton}
            </div>
          )}
          {/* Shifts Logs */}
          <div className="premium-card p-8">
            <h3 className="text-xl font-black mb-6 uppercase tracking-tight">{t('mt.log_title')}</h3>
            {props.isAdmin && props.hasShifts && m && (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 p-5 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900/60 dark:to-slate-900/30 border border-slate-200/60 dark:border-[#30363d] rounded-3xl animate-fadeIn text-right" data-testid="month-totals">
                <div className="col-span-1 md:col-span-4 flex justify-between items-center pb-2 border-b border-slate-200 dark:border-[#30363d]">
                  <span className="px-2 py-0.5 text-[9px] font-black uppercase bg-primary-100 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300 rounded-full">{t('mt.admin_badge')}</span>
                  <h4 className="text-xs font-black text-slate-800 dark:text-white">{t('mt.admin_title')}</h4>
                </div>
                <div className="p-3 bg-white dark:bg-[#070a0e] rounded-xl border border-slate-100 dark:border-[#30363d] space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 block">{t('mt.admin_opening')}</span>
                  <p className="text-sm font-black text-slate-800 dark:text-slate-200 font-mono">{m.opening.iqd} د.ع</p>
                  {showUsd && <p className="text-[10px] font-bold text-slate-400 font-mono">${m.opening.usd}</p>}
                </div>
                <div className="p-3 bg-white dark:bg-[#070a0e] rounded-xl border border-slate-100 dark:border-[#30363d] space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 block">{t('mt.admin_closing')}</span>
                  <p className="text-sm font-black text-slate-800 dark:text-slate-200 font-mono">{m.closing.iqd} د.ع</p>
                  {showUsd && <p className="text-[10px] font-bold text-slate-400 font-mono">${m.closing.usd}</p>}
                </div>
                {props.showProfits && (
                  <div className="p-3 bg-white dark:bg-[#070a0e] rounded-xl border border-slate-100 dark:border-[#30363d] space-y-1">
                    <span className="text-[10px] font-bold text-emerald-600 block">{t('mt.admin_profits')}</span>
                    <p className="text-sm font-black text-emerald-600 font-mono">+{m.profits.iqd} د.ع</p>
                    {showUsd && <p className="text-[10px] font-bold text-emerald-500 font-mono">${m.profits.usd}</p>}
                  </div>
                )}
                <div className="p-3 bg-white dark:bg-[#070a0e] rounded-xl border border-slate-100 dark:border-[#30363d] space-y-1">
                  <span className={`text-[10px] font-bold block ${!m.netIqdNegative ? 'text-emerald-500' : 'text-rose-500'}`}>{t('mt.admin_net')}</span>
                  <p className={`text-sm font-black font-mono ${!m.netIqdNegative ? 'text-emerald-600' : 'text-rose-600'}`}>{m.netIqd} د.ع</p>
                  {showUsd && <p className={`text-[10px] font-bold font-mono ${!m.netUsdNegative ? 'text-emerald-500' : 'text-rose-500'}`}>{m.netUsd}</p>}
                </div>
              </div>
            )}
            {/* Filters: Employee, From Date, To Date */}
            {props.hasShifts && (
              <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 bg-white dark:bg-[#161b22] border border-slate-200/60 dark:border-[#30363d] rounded-2xl animate-fadeIn text-right">
                <div>
                  <label className="block text-xs font-black text-slate-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('mt.filter_employee')}</label>
                  <select
                    value={props.employeeFilter}
                    onChange={(e) => props.onEmployeeFilter(e.target.value)}
                    className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all font-bold text-xs"
                    data-testid="shift-employee-filter"
                  >
                    <option value="">{t('mt.all_employees')}</option>
                    {props.employees.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('mt.from_date')}</label>
                  <EnglishDatePicker value={props.fromDate} onChange={(val) => props.onFromDate(val)} className="!py-2.5 !text-xs" />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest">{t('mt.to_date')}</label>
                  <EnglishDatePicker value={props.toDate} onChange={(val) => props.onToDate(val)} className="!py-2.5 !text-xs" />
                </div>
                {(props.employeeFilter || props.fromDate || props.toDate) && (
                  <div className="sm:col-span-3 flex justify-end">
                    <button type="button" onClick={props.onResetFilters} className="text-xs font-bold text-rose-600 dark:text-rose-450 hover:underline flex items-center gap-1.5">
                      <span>✕</span>
                      {t('mt.reset_filters')}
                    </button>
                  </div>
                )}
              </div>
            )}
            {!props.hasShifts ? (
              <p className="text-center py-8 text-xs text-slate-400">{t('mt.log_empty')}</p>
            ) : props.shiftRows.length === 0 ? (
              <p className="text-center py-8 text-xs text-slate-400">{t('mt.log_no_match')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-[#30363d] text-slate-400 dark:text-[#8b949e] font-black uppercase tracking-widest text-[10px]">
                      <th className="py-4 px-2">{t('mt.col_employee')}</th>
                      <th className="py-4 px-2">{t('mt.col_period')}</th>
                      <th className="py-4 px-2">{t('mt.col_opening')}</th>
                      <th className="py-4 px-2">{t('mt.col_expected')}</th>
                      <th className="py-4 px-2">{t('mt.col_actual')}</th>
                      <th className="py-4 px-2 text-center">{t('mt.col_status')}</th>
                      {props.showProfits && <th className="py-4 px-2 text-center">{t('mt.col_profits')}</th>}
                      <th className="py-4 px-2 text-center">{t('mt.col_report')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.shiftRows.map((s) => (
                      <tr key={s.id} className="border-b border-gray-50 dark:border-[#161b22] hover:bg-slate-50/50 dark:hover:bg-[#0d1117]/50 font-medium" data-testid="shift-row">
                        <td className="py-4 px-2 font-black text-slate-800 dark:text-white">{s.owner}</td>
                        <td className="py-4 px-2 space-y-0.5">
                          <p className="text-slate-500 dark:text-slate-400 font-mono">{s.startTime}</p>
                          <p className="text-[10px] text-slate-400 font-mono">{s.startDate}</p>
                          {!s.open && s.endTime && <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-mono font-bold">→ {s.endTime}</p>}
                        </td>
                        <td className="py-4 px-2 space-y-0.5 font-mono">
                          <p className="text-slate-700 dark:text-slate-300 font-black">{s.opening.iqd}</p>
                          {showUsd && <p className="text-[10px] text-blue-600">${s.opening.usd}</p>}
                        </td>
                        <td className="py-4 px-2 space-y-0.5 font-mono">
                          <p className="text-indigo-600 dark:text-indigo-400 font-black">{s.expected.iqd}</p>
                          {showUsd && <p className="text-[10px] text-indigo-500">${s.expected.usd}</p>}
                        </td>
                        <td className="py-4 px-2 space-y-0.5 font-mono">
                          {s.open || !s.counted ? (
                            <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 px-2.5 py-1 rounded-full">{t('mt.working')}</span>
                          ) : (
                            <>
                              <p className="text-slate-800 dark:text-white font-black">{s.counted.iqd}</p>
                              {showUsd && <p className="text-[10px] text-slate-500">${s.counted.usd}</p>}
                            </>
                          )}
                        </td>
                        <td className="py-4 px-2 text-center font-mono font-black">
                          {s.open ? (
                            s.diffIqd.zero && s.diffUsd.zero ? (
                              <span className="text-xs text-emerald-500 font-bold">{t('mt.opening_matched')}</span>
                            ) : (
                              <div className="inline-flex flex-col items-center">{diffCell(s.diffIqd, s.diffUsd, 'mt.matched')}</div>
                            )
                          ) : (
                            <div className="inline-flex flex-col items-center">{diffCell(s.diffIqd, s.diffUsd, 'mt.matched')}</div>
                          )}
                        </td>
                        {props.showProfits && <td className="py-4 px-2 text-center font-mono font-black text-amber-600 dark:text-amber-400">{s.profitsIqd} د.ع</td>}
                        <td className="py-4 px-2 text-center">
                          <button
                            type="button"
                            onClick={() => props.onShiftReport(s.id)}
                            className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-primary-100 hover:text-primary-600 rounded-xl transition-all"
                            title={t('mt.view_report')}
                            data-testid="shift-report-btn"
                          >
                            <FileText size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Day Matching */
        <div className="space-y-8">
          {activeDay ? (
            <div className="premium-card p-8 border-l-4 border-l-blue-500 relative overflow-hidden" data-testid="active-day">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -mr-16 -mt-16 blur-2xl pointer-events-none" />
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 pb-6 border-b border-slate-100 dark:border-slate-800">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                    <h3 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tight">{t('mt.day_pending_title', { date: activeDay.dateLabel })}</h3>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t('mt.day_pending_hint')}</p>
                </div>
                <button
                  type="button"
                  id="btn-reconcile-day"
                  onClick={props.onCloseDay}
                  className="w-full md:w-auto bg-blue-600 text-white px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-500 transition-all shadow-lg shadow-blue-500/20 active:scale-95"
                >
                  {t('mt.btn_close_day')}
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-6 animate-fadeIn">
                <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
                  <span className="text-[10px] font-bold text-slate-400 dark:text-[#8b949e] uppercase tracking-widest block mb-1">{t('mt.day_opening')}</span>
                  <div className="space-y-1 font-mono">
                    <p className="text-lg font-black text-slate-800 dark:text-white">{activeDay.opening.iqd} د.ع</p>
                    {showUsd && <p className="text-sm font-black text-blue-600 dark:text-blue-400">${activeDay.opening.usd}</p>}
                  </div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
                  <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest block mb-1">{t('mt.day_receipts')}</span>
                  <div className="space-y-1 font-mono">
                    <p className="text-lg font-black text-emerald-600">+{activeDay.receipts.iqd} د.ع</p>
                    {showUsd && <p className="text-sm font-black text-emerald-500">${activeDay.receipts.usd}</p>}
                  </div>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-[#0d1117] rounded-2xl border border-slate-100 dark:border-[#30363d]">
                  <span className="text-[10px] font-bold text-red-600 uppercase tracking-widest block mb-1">{t('mt.day_outflows')}</span>
                  <div className="space-y-1 font-mono">
                    <p className="text-lg font-black text-red-600">-{activeDay.outflows.iqd} د.ع</p>
                    {showUsd && <p className="text-sm font-black text-red-500">${activeDay.outflows.usd}</p>}
                  </div>
                </div>
                <div className="p-4 bg-primary-50/50 dark:bg-primary-950/20 rounded-2xl border border-primary-100/50 dark:border-primary-900/30">
                  <span className="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest block mb-1">{t('mt.day_expected')}</span>
                  <div className="space-y-1 font-mono">
                    <p className="text-lg font-black text-primary-600 dark:text-primary-400 digital-glow" data-testid="day-expected-iqd">
                      {activeDay.expected.iqd} د.ع
                    </p>
                    {showUsd && <p className="text-sm font-black text-primary-500">${activeDay.expected.usd}</p>}
                  </div>
                </div>
              </div>
              {/* Live Comparative Balances Grid for Admin Accounts Only */}
              {props.isAdmin && activeDay.grid && (
                <div className="mt-8 border-t border-slate-200 dark:border-[#30363d] pt-6 space-y-4 animate-fadeIn">
                  <div className="flex justify-between items-center bg-slate-50 dark:bg-slate-900/30 p-3 rounded-2xl border border-slate-100 dark:border-[#21262d]">
                    <span className="text-[9px] uppercase font-black text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/30 px-2.5 py-1 rounded-full">{t('mt.admin_only')}</span>
                    <h4 className="text-xs font-black text-slate-800 dark:text-slate-200 text-right uppercase tracking-wider">{t('mt.live_grid_title')}</h4>
                  </div>
                  <div className="overflow-hidden border border-slate-200 dark:border-[#30363d] rounded-2xl">
                    <table className="w-full text-right border-collapse text-[11px]">
                      <thead>
                        <tr className="bg-slate-50 dark:bg-[#161b22] text-slate-500 dark:text-[#8b949e] border-b border-slate-200 dark:border-[#30363d] font-black uppercase text-[9px] tracking-wider">
                          <th className="p-3 text-right">{t('mt.grid_account')}</th>
                          <th className="p-3 text-center">{t('mt.grid_opening')}</th>
                          <th className="p-3 text-center">{t('mt.grid_live')}</th>
                          <th className="p-3 text-left">{t('mt.grid_delta')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-[#30363d] font-mono">
                        {activeDay.grid.map((r) =>
                          r.usd ? (
                            <tr key={r.key} className="hover:bg-slate-50/50 dark:hover:bg-[#161b22]/10 text-blue-600 dark:text-blue-400">
                              <td className="p-3 text-right font-sans font-bold">{r.name}</td>
                              <td className="p-3 text-center">${r.opening}</td>
                              <td className="p-3 text-center font-black">${r.current}</td>
                              <td className={`p-3 text-left font-black ${r.deltaSign > 0 ? 'text-emerald-400' : r.deltaSign < 0 ? 'text-rose-400' : 'text-slate-400'}`}>{r.delta}</td>
                            </tr>
                          ) : (
                            <tr key={r.key} className="hover:bg-slate-50/50 dark:hover:bg-[#161b22]/10">
                              <td className="p-3 text-right font-sans font-bold text-slate-800 dark:text-slate-200">{r.name}</td>
                              <td className="p-3 text-center text-slate-500 dark:text-slate-400">{r.opening} د.ع</td>
                              <td className="p-3 text-center text-slate-900 dark:text-white font-black">{r.current} د.ع</td>
                              <td className={`p-3 text-left font-black ${r.deltaSign > 0 ? 'text-emerald-300' : r.deltaSign < 0 ? 'text-rose-400' : 'text-slate-400'}`}>{r.delta}</td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 dark:bg-slate-900/10 border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 rounded-[2.5rem] flex flex-col items-center justify-center text-center gap-4" data-testid="no-day">
              <div className="w-16 h-16 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-lg text-slate-400">
                <Calendar size={32} />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-800 dark:text-white">{t('mt.no_day')}</h3>
                <p className="text-xs text-slate-400 mt-1">{t('mt.no_day_hint')}</p>
              </div>
              {props.canOpenDay && (
                <button
                  type="button"
                  id="btn-open-day-match"
                  onClick={props.onOpenDay}
                  className="bg-primary-600 text-white px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-primary-500 transition-all shadow-md active:scale-95"
                >
                  {t('mt.btn_open_day')}
                </button>
              )}
            </div>
          )}
          {/* Day matches log */}
          <div className="premium-card p-8">
            <h3 className="text-xl font-black mb-6 uppercase tracking-tight">{t('mt.day_log_title')}</h3>
            {props.dayRows.length === 0 ? (
              <p className="text-center py-8 text-xs text-slate-400">{t('mt.day_log_empty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-[#30363d] text-slate-400 dark:text-[#8b949e] font-black uppercase tracking-widest text-[10px]">
                      <th className="py-4 px-2">{t('mt.dcol_date')}</th>
                      <th className="py-4 px-2">{t('mt.dcol_opening')}</th>
                      <th className="py-4 px-2">{t('mt.dcol_expected')}</th>
                      <th className="py-4 px-2">{t('mt.dcol_actual')}</th>
                      <th className="py-4 px-2 text-center">{t('mt.dcol_status')}</th>
                      <th className="py-4 px-2 text-center">{t('mt.dcol_print')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.dayRows.map((d) => (
                      <tr key={d.id} className="border-b border-gray-50 dark:border-[#161b22] hover:bg-slate-50/50 dark:hover:bg-[#0d1117]/50 font-medium" data-testid="day-row">
                        <td className="py-4 px-2 font-black text-slate-800 dark:text-white font-mono">{d.date}</td>
                        <td className="py-4 px-2 space-y-0.5 font-mono">
                          <p className="text-slate-700 dark:text-slate-300 font-black">{d.opening.iqd}</p>
                          {showUsd && <p className="text-[10px] text-blue-600">${d.opening.usd}</p>}
                        </td>
                        <td className="py-4 px-2 space-y-0.5 font-mono">
                          <p className="text-indigo-600 dark:text-indigo-400 font-black">{d.expected.iqd}</p>
                          {showUsd && <p className="text-[10px] text-indigo-500">${d.expected.usd}</p>}
                        </td>
                        <td className="py-4 px-2 space-y-0.5 font-mono">
                          {!d.open && d.counted ? (
                            <>
                              <p className="text-slate-800 dark:text-white font-black">{d.counted.iqd}</p>
                              {showUsd && <p className="text-[10px] text-slate-500">${d.counted.usd}</p>}
                            </>
                          ) : d.expired ? (
                            <span className="text-[10px] bg-rose-50 dark:bg-rose-950/40 text-rose-600 px-2.5 py-1 rounded-full">{t('mt.day_expired')}</span>
                          ) : (
                            <span className="text-[10px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 px-2.5 py-1 rounded-full">{t('mt.day_active')}</span>
                          )}
                        </td>
                        <td className="py-4 px-2 text-center font-mono font-black">
                          {d.open && !d.expired ? (
                            <span className="text-xs text-blue-500 font-bold">{t('mt.day_ongoing')}</span>
                          ) : d.expired ? (
                            <span className="text-xs text-rose-500 font-bold">{t('mt.day_expired')}</span>
                          ) : (
                            <div className="inline-flex flex-col items-center">{diffCell(d.diffIqd, d.diffUsd, 'mt.day_matched')}</div>
                          )}
                        </td>
                        <td className="py-4 px-2 text-center">
                          <button type="button" onClick={() => props.onDayReport(d.id)} className="p-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-xl transition-all" title={t('mt.details')} data-testid="day-report-btn">
                            <Info size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
